import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Filter } from "lucide-react";
import api from "../lib/api";
import ProfileCard from "../components/ProfileCard";
import useSEO from "../hooks/useSEO";

const TABS = [
  { key: "all", label: "Tous" },
  { key: "professional", label: "Professionnels" },
  { key: "entrepreneur", label: "Entrepreneurs" },
  { key: "organism", label: "Organismes" },
];

function FleurDeLys() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
      <path d="M12 2c-.9 0-1.7.9-1.7 2 0 .8.3 1.5.7 1.9-1.4.9-2 2.4-2 4.1 0 1.2.4 2.3 1 3.2H8v2h2v1H8v2h2v3.8l-1.5.5-.5 1 4 .5 4-.5-.5-1-1.5-.5V18h2v-2h-2v-1h2v-2h-2c.6-.9 1-2 1-3.2 0-1.7-.6-3.2-2-4.1.4-.4.7-1.1.7-1.9 0-1.1-.8-2-1.7-2zm-4 8c-1 0-2 .3-2.8.9C4.4 11.5 4 12.4 4 13.3c0 1.3.9 2.4 2 2.7 0-2.4 1-4.5 2-6zm8 0c1 1.5 2 3.6 2 6 1.1-.3 2-1.4 2-2.7 0-.9-.4-1.8-1.2-2.4-.8-.6-1.8-.9-2.8-.9z"/>
    </svg>
  );
}

export default function IciQuebec() {
  useSEO({
    title: "Ici Québec — Professionnels, entreprises et organismes",
    description: "Découvrez tous les prestataires, entreprises et organismes francophones du Québec sur DemkaBiz.",
  });
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = { province: "Québec", limit: 60 };
    if (tab !== "all") params.profile_type = tab;
    setLoading(true);
    api.get("/profiles", { params })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [tab]);

  const counts = useMemo(() => ({
    all: items.length,
  }), [items]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20" data-testid="ici-quebec-page">
      <div className="flex items-center gap-3 text-primary mb-3">
        <FleurDeLys />
        <p className="text-xs uppercase tracking-widest font-semibold">Ici Québec</p>
      </div>
      <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-foreground tracking-tight leading-[1.05]">
        Le Québec francophone,<br /><span className="text-primary">en un seul endroit</span>.
      </h1>
      <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed">
        Tous les professionnels, entreprises et organismes de la province du Québec, référencés et validés par notre équipe.
      </p>

      <div className="flex flex-wrap gap-2 mt-8" data-testid="ici-quebec-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`ici-quebec-tab-${t.key}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition ${tab === t.key ? "bg-primary text-primary-foreground shadow-sm" : "bg-white border border-primary/15 text-foreground/70 hover:bg-secondary"}`}
          >
            {t.label}
          </button>
        ))}
        <Link to="/annuaire/professionnels?province=Qu%C3%A9bec" className="ml-auto text-sm text-primary hover:underline inline-flex items-center gap-1">
          <Filter className="w-4 h-4" /> Ouvrir avec filtres complets
        </Link>
      </div>

      <p className="text-sm text-muted-foreground mt-6" data-testid="ici-quebec-count">
        {loading ? "Chargement…" : `${items.length} fiche${items.length > 1 ? "s" : ""} au Québec`}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
        {items.map((p) => <ProfileCard key={p.id} profile={p} />)}
      </div>
      {!loading && items.length === 0 && (
        <div className="text-center py-16 text-muted-foreground border border-dashed border-primary/20 rounded-2xl mt-6" data-testid="ici-quebec-empty">
          Aucune fiche pour cette catégorie au Québec pour l'instant.
        </div>
      )}
    </div>
  );
}
